import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../../api";
import type { Timesheet } from "../../api";
import { depuisEntries, memeGrille, versEntries } from "./mois";
import type { DemiJournees, Grille } from "./mois";

/** Où en est l'enregistrement. */
export type EtatEnregistrement = "propre" | "modifie" | "enregistrement" | "enregistre" | "erreur";

export type AutoSave = {
  valeurs: Grille;
  etat: EtatEnregistrement;
  /** Le refus du dernier enregistrement, ou `null`. */
  erreur: unknown;
  /** Change un jour ; l'enregistrement part après un court silence. */
  setJour: (jour: string, valeur: DemiJournees) => void;
  /**
   * Enregistre tout de suite ce qui est en attente et rend la feuille à jour,
   * ou `null` si l'enregistrement a échoué. À appeler avant de soumettre :
   * soumettre une grille que le serveur n'a pas encore reçue enverrait la
   * version précédente.
   */
  flush: () => Promise<Timesheet | null>;
};

const DELAI_MS = 800;

/**
 * Enregistrement automatique de la grille d'une feuille.
 *
 * Chaque clic modifie la grille locale tout de suite ; l'envoi part après un
 * silence de 800 ms, en une seule requête pour toute la grille — c'est le
 * contrat (`PUT entries` remplace tout), et c'est ce qui rend l'enregistrement
 * indifférent à l'ordre des clics. Une réponse en retard (deux enregistrements
 * qui se croisent) est ignorée : seule la dernière version envoyée compte.
 *
 * Le navigateur prévient avant de quitter tant qu'un envoi est en attente.
 */
export function useAutoSave(feuille: Timesheet): AutoSave {
  const [valeurs, setValeurs] = useState<Grille>(() => depuisEntries(feuille.entries));
  const [etat, setEtat] = useState<EtatEnregistrement>("propre");
  const [erreur, setErreur] = useState<unknown>(null);

  // La dernière grille enregistrée côté serveur, pour savoir si quelque chose
  // est en attente ; et un numéro de version pour ignorer une réponse périmée.
  const enregistree = useRef<Grille>(depuisEntries(feuille.entries));
  const version = useRef(0);
  const minuteur = useRef<ReturnType<typeof setTimeout> | null>(null);
  const derniere = useRef<Grille>(valeurs);
  derniere.current = valeurs;

  const enregistrer = useCallback(async (): Promise<Timesheet | null> => {
    if (minuteur.current !== null) {
      clearTimeout(minuteur.current);
      minuteur.current = null;
    }
    const grille = derniere.current;
    if (memeGrille(grille, enregistree.current)) {
      setEtat((e) => (e === "erreur" ? e : "propre"));
      return null;
    }
    const numero = ++version.current;
    setEtat("enregistrement");
    try {
      const maj = await api.saveTimesheetEntries(feuille.id, { entries: versEntries(grille) });
      if (numero !== version.current) {
        return maj;
      }
      enregistree.current = grille;
      setErreur(null);
      setEtat("enregistre");
      return maj;
    } catch (cause: unknown) {
      if (numero !== version.current) {
        return null;
      }
      setErreur(cause);
      setEtat("erreur");
      return null;
    }
  }, [feuille.id]);

  const setJour = useCallback(
    (jour: string, valeur: DemiJournees) => {
      setValeurs((avant) => ({ ...avant, [jour]: valeur }));
      setEtat("modifie");
      if (minuteur.current !== null) {
        clearTimeout(minuteur.current);
      }
      minuteur.current = setTimeout(() => void enregistrer(), DELAI_MS);
    },
    [enregistrer],
  );

  // Rien ne doit rester en attente au démontage : on envoie ce qu'on a.
  useEffect(
    () => () => {
      if (minuteur.current !== null) {
        clearTimeout(minuteur.current);
        void enregistrer();
      }
    },
    [enregistrer],
  );

  useEffect(() => {
    const enAttente = etat === "modifie" || etat === "enregistrement";
    if (!enAttente) {
      return;
    }
    const prevenir = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener("beforeunload", prevenir);
    return () => window.removeEventListener("beforeunload", prevenir);
  }, [etat]);

  const flush = useCallback(async (): Promise<Timesheet | null> => {
    if (memeGrille(derniere.current, enregistree.current)) {
      return null;
    }
    return enregistrer();
  }, [enregistrer]);

  return { valeurs, etat, erreur, setJour, flush };
}
